import SSENotificationService from '../../services/sseNotificationService'
import { RepositoryFactory } from '../../services/repositories/CommunicationAPI/RepositoryFactory'

const NotificationsRepo = RepositoryFactory.get('notifications')

const state = {
  notifications: [],
  unreadCount: 0,
  isConnected: false,
  loading: false,
  error: null
}

const getters = {
  allNotifications: state => state.notifications,
  unreadNotifications: state => state.notifications.filter(n => n.status === 'unread'),
  unreadCount: state => state.unreadCount,
  isConnected: state => state.isConnected,
  loading: state => state.loading,
  error: state => state.error
}

const mutations = {
  SET_NOTIFICATIONS(state, notifications) {
    state.notifications = notifications
  },
  ADD_NOTIFICATION(state, notification) {
    // Check if notification already exists (avoid duplicates)
    const exists = state.notifications.some(n => n.id === notification.id)
    if (!exists) {
      // Add to beginning of array (newest first)
      state.notifications.unshift(notification)
      // Increment unread count for new notifications
      if (notification.status === 'unread') {
        state.unreadCount++
      }
    }
  },
  UPDATE_NOTIFICATION(state, updatedNotification) {
    const index = state.notifications.findIndex(n => n.id === updatedNotification.id)
    if (index !== -1) {
      state.notifications.splice(index, 1, updatedNotification)
    }
  },
  REMOVE_NOTIFICATION(state, notificationId) {
    state.notifications = state.notifications.filter(n => n.id !== notificationId)
  },
  SET_UNREAD_COUNT(state, count) {
    state.unreadCount = count
  },
  INCREMENT_UNREAD_COUNT(state) {
    state.unreadCount++
  },
  DECREMENT_UNREAD_COUNT(state) {
    if (state.unreadCount > 0) {
      state.unreadCount--
    }
  },
  SET_CONNECTION_STATUS(state, status) {
    state.isConnected = status
  },
  SET_LOADING(state, loading) {
    state.loading = loading
  },
  SET_ERROR(state, error) {
    state.error = error
  },
  MARK_AS_READ(state, notificationId) {
    const notification = state.notifications.find(n => n.id === notificationId)
    if (notification && notification.status === 'unread') {
      notification.status = 'read'
      state.unreadCount--
    }
  },
  MARK_ALL_AS_READ(state) {
    state.notifications.forEach(n => {
      n.status = 'read'
    })
    state.unreadCount = 0
  }
}

const actions = {
  /**
   * Initialize SSE connection
   */
  async initSSEConnection({ commit, rootState }) {
    try {
      // First, get a short-lived stream token from the API
      const tokenResponse = await NotificationsRepo.getStreamToken()
      const streamToken = tokenResponse.data.token
      
      if (!streamToken) {
        console.error('Failed to get stream token')
        return
      }

      const streamUrl = NotificationsRepo.getStreamUrl()
      
      // Add SSE event listener
      SSENotificationService.addListener((event) => {
        if (event.type === 'message' && event.data) {
          const data = event.data
          
          if (data.type === 'notification') {
            commit('ADD_NOTIFICATION', data)
            
            // Show browser/system notification if permission granted and page is not focused
            if (Notification.permission === 'granted' && document.hidden) {
              const notification = new Notification(data.title || 'New Notification', {
                body: data.message || 'You have a new notification',
                icon: '/assets/img/icons/android-chrome-192x192.png',
                badge: '/assets/img/icons/android-chrome-192x192.png',
                tag: `notification-${data.id}`,
                requireInteraction: false,
                silent: false
              })
              
              // Click handler to focus the window and navigate
              notification.onclick = function(event) {
                event.preventDefault()
                window.focus()
                if (data.metadata && data.metadata.route) {
                  window.location.href = data.metadata.route
                } else {
                  window.location.href = '/notifications'
                }
                notification.close()
              }
            }
          } else if (data.type === 'connected') {
            commit('SET_CONNECTION_STATUS', true)
          }
        } else if (event.type === 'error') {
          commit('SET_CONNECTION_STATUS', false)
        } else if (event.type === 'connected') {
          commit('SET_CONNECTION_STATUS', true)
        }
      })
      
      // Connect to SSE with stream token
      SSENotificationService.connect(streamUrl, streamToken)
      
    } catch (error) {
      console.error('Error initializing SSE connection:', error)
      commit('SET_ERROR', error.message)
    }
  },

  /**
   * Disconnect SSE
   */
  disconnectSSE({ commit }) {
    SSENotificationService.disconnect()
    commit('SET_CONNECTION_STATUS', false)
  },

  /**
   * Fetch all notifications
   */
  async fetchNotifications({ commit }) {
    commit('SET_LOADING', true)
    try {
      const response = await NotificationsRepo.all()
      const notifications = response.data.results || response.data
      commit('SET_NOTIFICATIONS', notifications)
      
      // Update unread count
      const unreadCount = notifications.filter(n => n.status === 'unread').length
      commit('SET_UNREAD_COUNT', unreadCount)
    } catch (error) {
      console.error('Error fetching notifications:', error)
      commit('SET_ERROR', error.message)
    } finally {
      commit('SET_LOADING', false)
    }
  },

  /**
   * Fetch unread count
   */
  async fetchUnreadCount({ commit }) {
    try {
      const response = await NotificationsRepo.getUnreadCount()
      commit('SET_UNREAD_COUNT', response.data.count)
    } catch (error) {
      console.error('Error fetching unread count:', error)
    }
  },

  /**
   * Mark notification as read
   */
  async markAsRead({ commit }, notificationId) {
    try {
      await NotificationsRepo.markAsRead(notificationId)
      commit('MARK_AS_READ', notificationId)
    } catch (error) {
      console.error('Error marking notification as read:', error)
      commit('SET_ERROR', error.message)
    }
  },

  /**
   * Mark all notifications as read
   */
  async markAllAsRead({ commit }) {
    try {
      await NotificationsRepo.markAllAsRead()
      commit('MARK_ALL_AS_READ')
    } catch (error) {
      console.error('Error marking all notifications as read:', error)
      commit('SET_ERROR', error.message)
    }
  },

  /**
   * Delete notification
   */
  async deleteNotification({ commit }, notificationId) {
    try {
      await NotificationsRepo.delete(notificationId)
      commit('REMOVE_NOTIFICATION', notificationId)
    } catch (error) {
      console.error('Error deleting notification:', error)
      commit('SET_ERROR', error.message)
    }
  },

  /**
   * Request browser notification permission
   */
  async requestNotificationPermission() {
    if ('Notification' in window) {
      if (Notification.permission === 'default') {
        const permission = await Notification.requestPermission()
        if (permission === 'granted') {
          console.log('Browser notifications enabled')
          // Show a welcome notification
          new Notification('Notifications Enabled', {
            body: 'You will now receive notifications even when this page is in the background',
            icon: '/assets/img/icons/android-chrome-192x192.png'
          })
        }
      } else if (Notification.permission === 'granted') {
        console.log('Browser notifications already enabled')
      } else {
        console.log('Browser notifications denied')
      }
    } else {
      console.log('This browser does not support notifications')
    }
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
